<template>
  <div class="container-fluid py-4">
    <!-- 1. Cabecera de Identificación -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h2 class="mb-0">{{ cliente?.nombre }} <small class="text-muted">#{{ cliente?.codigo }}</small></h2>
        <p class="text-muted mb-0">RIF/Cédula: {{ cliente?.rif }} |
          <span :class="['badge', cliente?.estatus === 1 ? 'bg-success' : 'bg-danger']">
            {{ cliente?.estatus === 1 ? 'Activo' : 'Inactivo' }}
          </span>
        </p>
      </div>
      <div class="d-flex gap-2">
        <button @click="$router.push('/clientes')" class="btn btn-outline-secondary">
          <i class="fas fa-arrow-left"></i> Volver al Listado
        </button>
        <button @click="imprimirEstadoCuenta" class="btn btn-primary">
          <i class="fas fa-file-pdf"></i> Imprimir Estado de Cuenta
        </button>
      </div>
    </div>

    <!-- 2. Panel de Resumen Financiero -->
    <div v-if="credito" class="card shadow-sm mb-4">
      <!-- Cabecera Destacada: Disponible y Compromiso -->
      <div class="card-body bg-light rounded-top p-3 border-bottom">
        <div class="row text-center">
          <div class="col-6 border-right">
            <span class="text-xs font-weight-bold text-success text-uppercase d-block mb-1">Crédito Disponible</span>
            <span class="h4 mb-0 font-weight-bold text-success">${{ (credito.limite - credito.deuda).toFixed(2) }}</span>
          </div>
          <div class="col-6">
            <span class="text-xs font-weight-bold text-danger text-uppercase d-block mb-1">Compromiso Actual (Por pagar)</span>
            <span class="h4 mb-0 font-weight-bold text-danger">${{ (credito.deuda - credito.abonado).toFixed(2) }}</span>
          </div>
        </div>
      </div>

      <!-- Detalles Secundarios -->
      <div class="card-body p-3">
        <div class="row text-center text-md-left">
          <div class="col-6 col-md-3 mb-2 mb-md-0">
            <small class="text-muted d-block">Límite Aprobado</small>
            <strong class="text-gray-800">${{ credito.limite }}</strong>
          </div>
          <div class="col-6 col-md-3 mb-2 mb-md-0">
            <small class="text-muted d-block">Consumo Actual</small>
            <strong class="text-gray-800">${{ credito.deuda }}</strong>
          </div>
          <div class="col-6 col-md-3">
            <small class="text-muted d-block">Total Abonado</small>
            <strong class="text-gray-800">${{ credito.abonado }}</strong>
          </div>
          <div class="col-6 col-md-3">
            <small class="text-muted d-block">Vencimiento</small>
            <strong class="text-warning">{{ credito.fecha_vencimiento || 'N/A' }}</strong>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="alert alert-info mb-4 d-flex justify-content-between align-items-center">
      <span><strong>Cliente de Contado</strong> - Sin línea de crédito asignada.</span>
      <span class="h5 mb-0">Total Comprado: <strong>${{ cliente?.total_comprado || 0 }}</strong></span>
    </div>

    <!-- Banner de Saldo a Favor -->
    <div v-if="totalSaldoAFavor > 0" class="alert alert-warning border-warning shadow-sm mb-4">
      <i class="fas fa-exclamation-triangle mr-2"></i>
      <strong>Atención:</strong> El cliente tiene <strong>${{ totalSaldoAFavor.toFixed(2) }}</strong> en pagos sin
      asignar a ventas. La deuda se reducirá cuando aplique este saldo a facturas pendientes.
    </div>

    <!-- 3. Filtros de Búsqueda Rápida -->
    <div class="card mb-4 shadow-sm">
      <div class="card-body py-2">
        <div class="row g-3 align-items-center">
          <div class="col-auto">
            <label class="form-label small mb-0">Desde:</label>
            <input type="date" v-model="filtros.desde" class="form-control form-control-sm">
          </div>
          <div class="col-auto">
            <label class="form-label small mb-0">Hasta:</label>
            <input type="date" v-model="filtros.hasta" class="form-control form-control-sm">
          </div>
          <div class="col-md-4 ms-auto">
            <label class="form-label small mb-0">Buscador por Referencia:</label>
            <input type="text" v-model="filtros.busqueda" placeholder="Ej: FAC-001 o Pago #123"
              class="form-control form-control-sm">
          </div>
        </div>
      </div>
    </div>

    <!-- 4. Tabla Maestra de Transacciones -->
    <div class="card shadow border-0">
      <!-- VISTA ESCRITORIO -->
      <div class="table-responsive d-none d-md-block">
        <table class="table table-hover align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th>Fecha</th>
              <th>Referencia</th>
              <th>Tipo</th>
              <th>Método</th>
              <th>Monto</th>
              <th class="text-end">Compromiso Restante</th>
              <th class="text-center">Estatus</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in historialFiltrado" :key="item.ref + item.fecha"
              :class="{ 'table-danger opacity-75': (item.status_logistico || item.estatus)?.toLowerCase() === 'anulado' }">
              <td>{{ item.fecha }}</td>
              <td>
                <strong>{{ item.ref || 'N/A' }}</strong>
                <div v-if="item.categoria === 'pago' && item.saldo_disponible > 0" class="mt-1">
                  <span class="badge badge-pill badge-warning border border-dark"
                    title="Este dinero no ha sido cruzado con facturas"
                    v-tooltip="'Este dinero no ha sido cruzado con facturas'">
                    <i class="fas fa-coins mr-1"></i>
                    Por aplicar: ${{ parseFloat(item.saldo_disponible).toFixed(2) }}
                  </span>
                </div>
              </td>
              <td>
                <span v-if="item.categoria === 'venta'" class="badge bg-danger text-white px-3 py-2 text-uppercase shadow-sm">
                  <i class="fas fa-shopping-cart me-1"></i> Venta
                </span>
                <span v-else-if="item.categoria === 'pago'" class="badge bg-success text-white px-3 py-2 text-uppercase shadow-sm">
                  <i class="fas fa-hand-holding-usd me-1"></i> Pago
                </span>
                <span v-else class="badge bg-secondary text-white px-3 py-2 text-uppercase">
                  {{ item.categoria }}
                </span>
              </td>
              <td>{{ item.tipo_pago }}</td>
              <td class="fw-bold" :class="item.categoria === 'venta' ? 'text-danger' : 'text-success'">
                {{ item.categoria === 'venta' ? '+' : '-' }} ${{ parseFloat(item.total).toFixed(2) }}
              </td>
              <td class="text-end fw-bold text-dark">
                ${{ parseFloat(item.compromiso_acumulado || 0).toFixed(2) }}
              </td>
              <td class="text-center">
                <span
                  :class="['badge rounded-pill border px-3 py-2',
                    (item.status_logistico || item.estatus)?.toLowerCase() === 'anulado' ? 'bg-danger text-white' : 'bg-light text-dark']">
                  {{ item.status_logistico || item.estatus }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- VISTA MÓVIL -->
      <div class="d-md-none">
        <div v-for="item in historialFiltrado" :key="item.ref + item.fecha" class="p-3 border-bottom position-relative"
          :class="{ 'bg-light opacity-75': (item.status_logistico || item.estatus)?.toLowerCase() === 'anulado' }">

          <div class="d-flex justify-content-between align-items-center mb-2">
            <small class="text-muted"><i class="far fa-calendar-alt mr-1"></i> {{ item.fecha }}</small>
            <div>
              <span v-if="item.categoria === 'venta'" class="badge bg-danger text-white text-uppercase me-1">
                <i class="fas fa-shopping-cart me-1"></i> Venta
              </span>
              <span v-else-if="item.categoria === 'pago'" class="badge bg-success text-white text-uppercase me-1">
                <i class="fas fa-hand-holding-usd me-1"></i> Pago
              </span>
              <span
                :class="['badge rounded-pill px-2',
                  (item.status_logistico || item.estatus)?.toLowerCase() === 'anulado' ? 'bg-danger text-white' : 'bg-secondary text-white']"
                style="font-size: 0.7rem;">
                {{ item.status_logistico || item.estatus }}
              </span>
            </div>
          </div>

          <div class="d-flex justify-content-between align-items-end">
            <div>
              <div class="fw-bold text-navy" style="font-size: 1.1rem;">{{ item.ref }}</div>
              <small class="text-muted text-uppercase d-block">
                Método: {{ item.tipo_pago }}
              </small>
            </div>
            <div class="text-end">
              <h5 class="mb-0 fw-bold" :class="item.categoria === 'venta' ? 'text-danger' : 'text-success'">
                {{ item.categoria === 'venta' ? '+' : '-' }} ${{ parseFloat(item.total).toFixed(2) }}
              </h5>
              <small v-if="item.categoria === 'pago' && item.saldo_disponible > 0" class="text-orange font-weight-bold d-block">
                <i class="fas fa-coins"></i> Por aplicar: ${{ item.saldo_disponible }}
              </small>
            </div>
          </div>

          <div class="d-flex justify-content-between align-items-center mt-2 pt-2 border-top bg-light px-2 rounded">
            <small class="text-muted font-weight-bold">Compromiso Restante:</small>
            <span class="fw-bold text-dark">${{ parseFloat(item.compromiso_acumulado || 0).toFixed(2) }}</span>
          </div>

          <div v-if="(item.status_logistico || item.estatus)?.toLowerCase() === 'anulado'"
            class="position-absolute w-100 h-100 d-flex align-items-center justify-content-center"
            style="top:0; left:0; pointer-events:none; opacity: 0.1;">
            <h1 class="text-uppercase font-weight-bold" style="transform: rotate(-15deg);">ANULADO</h1>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/services/api";

export default {
  props: ['id'],
  data() {
    return {
      cliente: {},
      credito: null,
      historial: [],
      loading: false,
      filtros: {
        desde: '',
        hasta: '',
        busqueda: ''
      }
    };
  },
  computed: {
    historialFiltrado() {
      // 1. Filtrar por fecha y búsqueda
      let items = this.historial.filter(item => {
        const fechaItem = new Date(item.fecha);
        const desde = this.filtros.desde ? new Date(this.filtros.desde) : null;
        const hasta = this.filtros.hasta ? new Date(this.filtros.hasta) : null;

        const matchBusqueda = (item.ref || '').toLowerCase().includes((this.filtros.busqueda || '').toLowerCase());
        const matchDesde = !desde || fechaItem >= desde;
        const matchHasta = !hasta || fechaItem <= hasta;
        return matchBusqueda && matchDesde && matchHasta;
      });

      // 2. Ordenar de la más antigua a la más reciente para calcular el saldo acumulado correctamente
      items.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));

      // 3. Calcular el Compromiso Acumulado paso a paso
      let acumulado = 0;
      items = items.map(item => {
        const esAnulado = (item.status_logistico || item.estatus)?.toLowerCase() === 'anulado';
        
        if (!esAnulado) {
          if (item.categoria === 'venta') {
            acumulado += parseFloat(item.total || 0);
          } else if (item.categoria === 'pago') {
            acumulado -= parseFloat(item.total || 0);
          }
        }

        return {
          ...item,
          compromiso_acumulado: acumulado
        };
      });

      // 4. Invertir para mostrar las transacciones más recientes primero
      return items.reverse();
    },
    totalSaldoAFavor() {
      return this.historial
        .filter(item => item.categoria === 'pago' && item.saldo_disponible > 0)
        .reduce((acc, item) => acc + parseFloat(item.saldo_disponible), 0);
    }
  },
  methods: {
    async fetchHistorial() {
      this.loading = true;
      try {
        const { data } = await api.get(`/clientes/${this.id}/historial-completo`);
        this.cliente = data.cliente;
        this.credito = data.creditos && data.creditos.length > 0 ? data.creditos[0] : null;
        this.historial = data.historial;
      } catch (error) {
        console.error("Error cargando expediente", error);
      } finally {
        this.loading = false;
      }
    },
    imprimirEstadoCuenta() { window.print(); }
  },
  mounted() { this.fetchHistorial(); }
}
</script>